import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
    Button,
    Checkbox,
    Flex,
    Group,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';

import { Request, Store } from '../../../codegen/graphql';
import { GET_AD_ZONE_STORES_FOR_REQUEST } from '../../../apollo/queries/priceChangeRequest';
import { useAppConfig } from '../../../hooks/useAppConfig';
import {
    isPnlStoreTradingAsCashbuild,
    shouldEnablePnlPriceInput,
} from '../../../helpers/storeHelpers';
import { getQuickActionNewPriceSeed } from '../../../helpers/pricingBatchIndicatorHelpers';
import {
    getRequestEffectiveCostPrice,
    getRequestZoneHighestCostPrice,
} from '../../../helpers/requestZonePricing';
import { getHighestCostPrice } from '../../../helpers/priceCalculation';
import { getCurrencyFromRequest } from '../../../utility/getProductCurrency';
import { When } from '../../common';
import BelowCostApprovalAlert from '../BelowCostApprovalAlert';
import { PriceChangeRequestMetadata } from '../../../types/price-change-request';
import {
    BELOW_COST_INTERIM_PRICE_ERROR,
    BELOW_COST_NEW_PRICE_ERROR,
    isPriceBelowCostExVat,
    MAX_SAFE_PRICE,
    toFiniteNumber,
} from '../../../helpers/calculateMargin';
import { QuickActionVerb, useApplyQuickAction } from './useApplyQuickAction';
import MarginCalculation from '../components/MarginCalculation';
import { QuickActionFormStoreSelection } from './QuickActionFormStoreSelection';
import { useHighestCostPriceStore } from '../../../hooks/useHighestCostPriceStore';
import { useEligibleAdvertisingZoneStores } from '../../../hooks/useEligibleAdvertisingZoneStores';
import { useUser } from '../../../hooks/useUser';
import { useOptionalPricingBatchStaging } from '../batches/PricingBatchStagingContext';
import { queuedRequestLabel } from '../batches/pricingBatchStaging.types';
import { requestComboboxSelectClassNames } from '../components/requestComboboxClassNames';
import {
    filterStoreIdsToEligibleSet,
    parseApplicableTo,
} from '../../../helpers/storeSelectionHelper';
import {
    getPriceChangeScope,
    getPriceChangeScopeLabel,
    getSuggestedStoreIds,
    PRICE_CHANGE_SCOPE,
} from '../../../helpers/priceChangeScope';
import {
    quickActionFormContentWidthClassName,
    quickActionFormFieldClassName,
    quickActionFormFieldFullWidthClassName,
    quickActionFormFieldNarrowClassName,
    quickActionFormFieldsBlockClassName,
    quickActionFormFieldsShellClassName,
    quickActionFormMarginCardClassName,
    quickActionFormNotesClassName,
    quickActionFormPairRowClassName,
    quickActionFormStackClassName,
} from './quickActionFormLayout';

type QuickActionRequest = Request & {
    resolvedVatPercentage?: number | null;
};

type Props = {
    request: QuickActionRequest;
    verb: Exclude<QuickActionVerb, 'decline'>;
    onCancel: () => void;
    onSuccess?: () => void;
};

type ApplicableTo = 'zone' | 'store';

type FormValues = {
    applicableTo: ApplicableTo;
    selectedStoreIds: string[];
    newPrice: number | undefined;
    reason: string;
    priceChangeDurationStart: Date | null;
    priceChangeDurationEnd: Date | null;
    noEndDate: boolean;
    notes: string;
};

function isStorePnlOnly(store: Store | null | undefined): boolean {
    if (!store) {
        return false;
    }
    const isPnl = store.storeNo?.startsWith('P') ?? false;
    return isPnl && !isPnlStoreTradingAsCashbuild(store);
}

export function QuickActionForm({ request, verb, onCancel, onSuccess }: Props) {
    const { interimPriceReasons, vatPercentage } = useAppConfig();
    const batchStaging = useOptionalPricingBatchStaging();
    const { isStoreManager, isDivisionManager, isOpsExecutive } = useUser();
    const canApproveBelowCost = isDivisionManager || isOpsExecutive;
    const { submit, loading, resolvedAction } = useApplyQuickAction(
        request,
        verb,
    );

    const requestStore = (request.store ?? null) as Store | null;
    const applicableToDirtyRef = useRef(false);
    const selectedStoresDirtyRef = useRef(false);
    const syncedNewPriceSeedRef = useRef<number | undefined | null>(null);

    const { data: adZoneData, loading: adZoneStoresLoading } = useQuery(
        GET_AD_ZONE_STORES_FOR_REQUEST,
        {
            variables: {
                requestId: request.id,
                productSku: request.productSku,
            },
            fetchPolicy: 'cache-and-network',
        },
    );

    const advertisingZoneStores: Store[] = useMemo(() => {
        if (adZoneData) {
            return (adZoneData.getAdvertisingZoneStoresForRequest ??
                []) as Store[];
        }
        return (request.advertisingZoneStores ?? []) as Store[];
    }, [adZoneData, request.advertisingZoneStores]);

    const pnlOnlyStore = isStorePnlOnly(requestStore);

    const {
        eligibleZoneStores,
        eligibleZoneStoreIds,
        hasEligibleZoneStores,
        defaultApplicableTo,
    } = useEligibleAdvertisingZoneStores(advertisingZoneStores);

    const allowZoneOption = !pnlOnlyStore && hasEligibleZoneStores;

    const segmentedData = useMemo(() => {
        const items: { value: ApplicableTo; label: string }[] = [];
        if (allowZoneOption) {
            items.push({ value: 'zone', label: 'Advertising Zone' });
        }
        items.push({ value: 'store', label: 'Store' });
        return items;
    }, [allowZoneOption]);

    const dmSuggestedScope = getPriceChangeScope(request);
    const dmSuggestedStoreIds = getSuggestedStoreIds(request);
    const dmSuggestedScopeLabel = getPriceChangeScopeLabel(dmSuggestedScope);

    let initialApplicableTo: ApplicableTo = defaultApplicableTo;
    if (dmSuggestedScope === PRICE_CHANGE_SCOPE.STORE || !allowZoneOption) {
        initialApplicableTo = 'store';
    }

    const initialSelectedStoreIds = useMemo(() => {
        const suggested = filterStoreIdsToEligibleSet(
            dmSuggestedStoreIds,
            eligibleZoneStores,
        );
        if (suggested.length > 0) {
            return suggested;
        }
        return eligibleZoneStoreIds;
    }, [dmSuggestedStoreIds, eligibleZoneStoreIds, eligibleZoneStores]);

    const highestCostPriceStoreId =
        useHighestCostPriceStore(eligibleZoneStores);

    const fieldRadius = 'md' as const;
    const compactBtnClass = 'shrink-0 !min-h-11 !w-auto rounded-full px-8';
    const productCurrency = useMemo(
        () => getCurrencyFromRequest(request),
        [request],
    );

    const newPriceSeed = useMemo(() => {
        if (verb === 'approve') {
            const proposed = request.proposedPrice;
            if (
                typeof proposed === 'number' &&
                Number.isFinite(proposed) &&
                proposed > 0
            ) {
                return proposed;
            }
            return undefined;
        }
        return getQuickActionNewPriceSeed(request);
    }, [
        verb,
        request.id,
        request.proposedPrice,
        request.temporaryChangedPrice,
        request.temporaryCashbuildPrice,
        request.temporaryPnlPrice,
        request.store?.storeNo,
        request.store?.isTradingAsCashbuild,
        request.storeNo,
    ]);

    const stagedEntry = batchStaging?.getQueuedEntry(request.id);
    const reopeningStagedForm = stagedEntry?.verb === verb;

    const defaultFormValues: FormValues = {
        applicableTo: initialApplicableTo,
        selectedStoreIds: initialSelectedStoreIds,
        newPrice: newPriceSeed,
        reason: interimPriceReasons[0] ?? '',
        priceChangeDurationStart: request.proposedValidFrom
            ? dayjs(request.proposedValidFrom).toDate()
            : dayjs().add(1, 'day').toDate(),
        priceChangeDurationEnd: null,
        noEndDate: false,
        notes: '',
    };

    let formInitialValues = defaultFormValues;
    if (reopeningStagedForm && stagedEntry) {
        const { metadata, notes } = stagedEntry;
        const noEndDate = Boolean(
            metadata.changedPriceValidTo &&
                dayjs(metadata.changedPriceValidTo).isSame(
                    dayjs('9999-12-31'),
                    'day',
                ),
        );
        let priceChangeDurationEnd: Date | null = null;
        if (noEndDate) {
            priceChangeDurationEnd = dayjs('9999-12-31').toDate();
        } else if (metadata.changedPriceValidTo) {
            priceChangeDurationEnd = dayjs(
                metadata.changedPriceValidTo,
            ).toDate();
        }

        const restoredStoreIds = filterStoreIdsToEligibleSet(
            metadata.applyToStores,
            eligibleZoneStores,
        );

        formInitialValues = {
            applicableTo:
                allowZoneOption && restoredStoreIds.length > 0
                    ? 'zone'
                    : 'store',
            selectedStoreIds:
                restoredStoreIds.length > 0
                    ? restoredStoreIds
                    : defaultFormValues.selectedStoreIds,
            newPrice:
                (pnlOnlyStore ? metadata.pnlPrice : metadata.cashbuildPrice) ??
                metadata.proposedPrice ??
                defaultFormValues.newPrice,
            reason: metadata.reason ?? defaultFormValues.reason,
            priceChangeDurationStart: metadata.changedPriceValidFrom
                ? dayjs(metadata.changedPriceValidFrom).toDate()
                : defaultFormValues.priceChangeDurationStart,
            priceChangeDurationEnd,
            noEndDate,
            notes,
        };
    }

    const form = useForm<FormValues>({
        initialValues: formInitialValues,
        validate: values => {
            const vat = request.resolvedVatPercentage ?? vatPercentage;
            const costPrice = getRequestZoneHighestCostPrice(request);
            const newPrice = toFiniteNumber(values.newPrice);
            let newPriceError: string | null =
                newPrice === null || newPrice <= 0
                    ? 'New price must be greater than 0'
                    : null;
            if (
                !newPriceError &&
                !canApproveBelowCost &&
                isPriceBelowCostExVat(newPrice, costPrice, vat)
            ) {
                newPriceError =
                    verb === 'interim'
                        ? BELOW_COST_INTERIM_PRICE_ERROR
                        : BELOW_COST_NEW_PRICE_ERROR;
            }

            return {
                selectedStoreIds:
                    values.applicableTo === 'zone' &&
                    filterStoreIdsToEligibleSet(
                        values.selectedStoreIds,
                        eligibleZoneStores,
                    ).length === 0
                        ? 'Select at least one store in the zone.'
                        : null,
                newPrice: newPriceError,
                reason:
                    verb === 'interim' && !values.reason
                        ? 'Reason is required.'
                        : null,
                priceChangeDurationStart: values.priceChangeDurationStart
                    ? null
                    : 'Start date is required.',
                priceChangeDurationEnd:
                    values.noEndDate || values.priceChangeDurationEnd
                        ? null
                        : 'End date is required.',
            };
        },
        validateInputOnBlur: true,
        validateInputOnChange: ['newPrice'],
    });

    useEffect(() => {
        syncedNewPriceSeedRef.current = null;
    }, [request.id]);

    useEffect(() => {
        if (reopeningStagedForm || newPriceSeed === undefined) {
            return;
        }
        if (syncedNewPriceSeedRef.current === newPriceSeed) {
            return;
        }
        syncedNewPriceSeedRef.current = newPriceSeed;
        form.setFieldValue('newPrice', newPriceSeed);
    }, [newPriceSeed, reopeningStagedForm]);

    useEffect(() => {
        if (adZoneStoresLoading) {
            return;
        }

        if (reopeningStagedForm && stagedEntry) {
            if (
                applicableToDirtyRef.current ||
                selectedStoresDirtyRef.current
            ) {
                return;
            }

            const restoredStoreIds = filterStoreIdsToEligibleSet(
                stagedEntry.metadata.applyToStores,
                eligibleZoneStores,
            );
            if (allowZoneOption && restoredStoreIds.length > 0) {
                form.setFieldValue('applicableTo', 'zone');
                form.setFieldValue('selectedStoreIds', restoredStoreIds);
                applicableToDirtyRef.current = true;
                selectedStoresDirtyRef.current = true;
                return;
            }

            form.setFieldValue('applicableTo', 'store');
            return;
        }

        if (!applicableToDirtyRef.current) {
            form.setFieldValue('applicableTo', initialApplicableTo);
        }

        if (!hasEligibleZoneStores) {
            return;
        }

        if (!selectedStoresDirtyRef.current) {
            form.setFieldValue('selectedStoreIds', initialSelectedStoreIds);
            return;
        }

        const filteredSelectedStoreIds = filterStoreIdsToEligibleSet(
            form.values.selectedStoreIds,
            eligibleZoneStores,
        );
        if (
            filteredSelectedStoreIds.length !==
            form.values.selectedStoreIds.length
        ) {
            form.setFieldValue('selectedStoreIds', filteredSelectedStoreIds);
        }
    }, [
        adZoneStoresLoading,
        allowZoneOption,
        defaultApplicableTo,
        eligibleZoneStoreIds,
        eligibleZoneStores,
        hasEligibleZoneStores,
        initialApplicableTo,
        initialSelectedStoreIds,
        reopeningStagedForm,
        stagedEntry,
    ]);

    const resolvedVatPercentage =
        request.resolvedVatPercentage ?? vatPercentage;

    const marginCostPrice = useMemo(() => {
        let selectedStores: Store[] = [];
        if (form.values.applicableTo === 'store') {
            if (requestStore) {
                selectedStores = [requestStore];
            }
        } else {
            selectedStores = eligibleZoneStores.filter(zoneStore =>
                form.values.selectedStoreIds.includes(zoneStore.id),
            );
        }
        const selectedCost =
            selectedStores.length > 0
                ? getHighestCostPrice(selectedStores)
                : null;
        return selectedCost ?? getRequestEffectiveCostPrice(request);
    }, [
        eligibleZoneStores,
        form.values.applicableTo,
        form.values.selectedStoreIds,
        request,
        requestStore,
    ]);
    const isBelowCost = isPriceBelowCostExVat(
        form.values.newPrice,
        marginCostPrice,
        resolvedVatPercentage,
    );

    const marginPriceType = useMemo(() => {
        if (pnlOnlyStore) {
            return 'pnl' as const;
        }
        if (requestStore && shouldEnablePnlPriceInput(requestStore)) {
            return 'pnl' as const;
        }
        return 'cashbuild' as const;
    }, [pnlOnlyStore, requestStore]);

    const onChangeNoEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue('noEndDate', event.currentTarget.checked);
        form.setFieldValue(
            'priceChangeDurationEnd',
            event.currentTarget.checked ? dayjs('9999-12-31').toDate() : null,
        );
    };

    const [submitting, setSubmitting] = useState(false);

    const buildMetadata = (values: FormValues): PriceChangeRequestMetadata => {
        const newPrice = Number(values.newPrice);
        const usePnl = pnlOnlyStore;
        const applyToStores =
            values.applicableTo === 'zone' && allowZoneOption
                ? filterStoreIdsToEligibleSet(
                      values.selectedStoreIds,
                      eligibleZoneStores,
                  )
                : undefined;

        let changedPriceValidTo: string;
        if (values.noEndDate) {
            changedPriceValidTo = dayjs('9999-12-31').toISOString();
        } else if (values.priceChangeDurationEnd) {
            changedPriceValidTo = dayjs(
                values.priceChangeDurationEnd,
            ).toISOString();
        } else {
            changedPriceValidTo = dayjs().toISOString();
        }

        const metadata: PriceChangeRequestMetadata = {
            cashbuildPrice: usePnl ? undefined : newPrice,
            pnlPrice: usePnl ? newPrice : undefined,
            changedPriceValidFrom: values.priceChangeDurationStart
                ? dayjs(values.priceChangeDurationStart).toISOString()
                : dayjs().toISOString(),
            changedPriceValidTo,
            applyToStores,
        };

        if (verb === 'interim') {
            metadata.reason = values.reason;
        }

        if (verb === 'approve') {
            metadata.proposedPrice = newPrice;
        }

        return metadata;
    };

    const handleSubmit = form.onSubmit(async values => {
        setSubmitting(true);
        const outcome =
            verb === 'interim' ? 'ApprovedWithTemporaryPrice' : 'Approved';
        const metadata = buildMetadata(values);
        const { notes } = values;

        if (batchStaging) {
            batchStaging.enqueue({
                requestId: request.id,
                requestLabel: queuedRequestLabel(request),
                verb,
                outcome,
                notes,
                metadata,
            });
            setSubmitting(false);
            onSuccess?.();
            return;
        }

        const ok = await submit({
            outcome,
            notes,
            metadata,
        });
        setSubmitting(false);
        if (ok) {
            onSuccess?.();
        }
    });

    const proceedDisabled =
        loading ||
        submitting ||
        !resolvedAction?.canSubmit ||
        (!canApproveBelowCost && isBelowCost);

    return (
        <form
            onSubmit={handleSubmit}
            className="flex min-h-0 min-w-0 w-full max-w-full flex-col gap-5 overflow-x-hidden px-4 py-3"
        >
            <div className="flex w-full min-w-0 max-w-full flex-col items-start gap-5">
                <When condition={segmentedData.length > 1}>
                    <Stack gap="md">
                        <Text size="md" fw={500}>
                            Adjustment Applicable to
                        </Text>
                        <SegmentedControl
                            data={segmentedData}
                            color="primary"
                            size="md"
                            radius="md"
                            value={form.values.applicableTo}
                            onChange={value => {
                                applicableToDirtyRef.current = true;
                                const nextApplicableTo =
                                    parseApplicableTo(value);
                                if (!nextApplicableTo) {
                                    return;
                                }
                                form.setFieldValue(
                                    'applicableTo',
                                    nextApplicableTo,
                                );
                            }}
                            classNames={{
                                root: `self-start ${quickActionFormContentWidthClassName}`,
                                control: '!min-h-10',
                            }}
                        />
                        <When condition={Boolean(dmSuggestedScopeLabel)}>
                            <Text size="sm" c="dimmed">
                                DM suggested: {dmSuggestedScopeLabel}
                            </Text>
                        </When>
                    </Stack>
                </When>

                <div className={quickActionFormFieldsShellClassName}>
                    <div
                        className={`${quickActionFormFieldsBlockClassName} ${quickActionFormContentWidthClassName}`}
                    >
                        <div className={quickActionFormStackClassName}>
                            <QuickActionFormStoreSelection
                                applicableTo={form.values.applicableTo}
                                selectedStoreIds={form.values.selectedStoreIds}
                                onSelectedStoreIdsChange={ids => {
                                    selectedStoresDirtyRef.current = true;
                                    form.setFieldValue('selectedStoreIds', ids);
                                }}
                                selectedStoreIdsError={
                                    form.errors.selectedStoreIds as
                                        | string
                                        | undefined
                                }
                                eligibleZoneStores={eligibleZoneStores}
                                highestCostPriceStoreId={
                                    highestCostPriceStoreId
                                }
                                requestStore={requestStore}
                                requestStoreName={request.storeName}
                                requestStoreNo={request.storeNo}
                                fieldRadius={fieldRadius}
                            />

                            <When condition={verb === 'interim'}>
                                <div
                                    className={
                                        quickActionFormFieldFullWidthClassName
                                    }
                                >
                                    <Select
                                        label="Reason for Interim"
                                        size="sm"
                                        radius={fieldRadius}
                                        required
                                        data={interimPriceReasons.map(
                                            reason => ({
                                                value: reason,
                                                label: reason,
                                            }),
                                        )}
                                        comboboxProps={{
                                            withinPortal: true,
                                            zIndex: 1000,
                                            width: 'target',
                                        }}
                                        classNames={
                                            requestComboboxSelectClassNames
                                        }
                                        className="w-full min-w-0"
                                        {...form.getInputProps('reason')}
                                    />
                                </div>
                            </When>

                            <When condition={!isStoreManager}>
                                <div
                                    className={
                                        quickActionFormMarginCardClassName
                                    }
                                >
                                    <MarginCalculation
                                        costPrice={marginCostPrice}
                                        currentSellingPrice={
                                            request.currentPrice ?? 0
                                        }
                                        proposedPrice={
                                            form.values.newPrice ?? 0
                                        }
                                        benchmarkMargin={
                                            request.benchmarkMargin ?? 0
                                        }
                                        currency={productCurrency}
                                        completed={request.completed}
                                        previousCostPrice={
                                            request.previousCostPrice
                                        }
                                        previousSellingPrice={
                                            request.previousSellingPrice
                                        }
                                        previousCashbuildPrice={
                                            request.previousCashbuildPrice
                                        }
                                        previousPnlPrice={
                                            request.previousPnlPrice
                                        }
                                        priceType={marginPriceType}
                                        layout="row"
                                        vatPercentage={resolvedVatPercentage}
                                    />
                                </div>
                            </When>

                            <NumberInput
                                label="New Cashbuild Price"
                                size="sm"
                                radius={fieldRadius}
                                required
                                decimalScale={2}
                                min={0}
                                max={MAX_SAFE_PRICE}
                                step={1}
                                className={
                                    isBelowCost && !form.errors.newPrice
                                        ? `${quickActionFormFieldClassName} [--mantine-color-error:var(--mantine-color-yellow-7)]`
                                        : quickActionFormFieldClassName
                                }
                                {...form.getInputProps('newPrice')}
                                error={form.errors.newPrice}
                            />
                            <When
                                condition={
                                    canApproveBelowCost &&
                                    isBelowCost &&
                                    !form.errors.newPrice
                                }
                            >
                                <BelowCostApprovalAlert />
                            </When>

                            <div
                                className={`${quickActionFormPairRowClassName} ${quickActionFormContentWidthClassName}`}
                            >
                                <DatePickerInput
                                    label="Start Date"
                                    size="sm"
                                    radius={fieldRadius}
                                    required
                                    minDate={dayjs().toDate()}
                                    leftSection={<IconCalendar size={16} />}
                                    popoverProps={{
                                        withinPortal: true,
                                        zIndex: 1000,
                                    }}
                                    className={
                                        quickActionFormFieldNarrowClassName
                                    }
                                    {...form.getInputProps(
                                        'priceChangeDurationStart',
                                    )}
                                />
                                <Flex
                                    direction="column"
                                    gap={8}
                                    className={
                                        quickActionFormFieldNarrowClassName
                                    }
                                >
                                    <DatePickerInput
                                        label="End Date"
                                        size="sm"
                                        radius={fieldRadius}
                                        required={!form.values.noEndDate}
                                        disabled={form.values.noEndDate}
                                        minDate={
                                            form.values.priceChangeDurationStart
                                                ? dayjs(
                                                      form.values
                                                          .priceChangeDurationStart,
                                                  )
                                                      .add(1, 'day')
                                                      .toDate()
                                                : dayjs().toDate()
                                        }
                                        leftSection={<IconCalendar size={16} />}
                                        popoverProps={{
                                            withinPortal: true,
                                            zIndex: 1000,
                                        }}
                                        className="w-full min-w-0"
                                        {...form.getInputProps(
                                            'priceChangeDurationEnd',
                                        )}
                                    />
                                    <Checkbox
                                        label="No end date"
                                        size="sm"
                                        checked={form.values.noEndDate}
                                        onChange={onChangeNoEndDate}
                                    />
                                </Flex>
                            </div>

                            <Textarea
                                label="Notes"
                                size="sm"
                                radius={fieldRadius}
                                placeholder="Additional Notes"
                                minRows={2}
                                autosize
                                className={`box-border block ${quickActionFormNotesClassName}`}
                                classNames={{
                                    root: `box-border block ${quickActionFormNotesClassName}`,
                                    input: 'min-w-0',
                                    wrapper: 'box-border w-full',
                                }}
                                {...form.getInputProps('notes')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Group gap="md" justify="flex-start" wrap="nowrap" className="pt-2">
                <Button
                    variant="outline"
                    size="md"
                    radius="xl"
                    type="button"
                    onClick={onCancel}
                    disabled={loading || submitting}
                    className={compactBtnClass}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    size="md"
                    radius="xl"
                    loading={loading || submitting}
                    disabled={proceedDisabled}
                    className={compactBtnClass}
                >
                    Proceed
                </Button>
            </Group>
        </form>
    );
}

export default QuickActionForm;
